// Technical visits are only for cumulative timing/deduplication, never extra score.
export function createReportTracker({ token, preview=false, enabled=true, isAtEnd=()=>false, env: suppliedEnv }) {
    if (preview || !token) return { start() {}, stop() {}, event() {} }
    let storage
    if (!suppliedEnv) { try { storage=window.sessionStorage } catch { /* restricted browser storage */ } }
    if (!enabled) {
        // Do not replay pre-send actions when the same report is opened after sending.
        try { (suppliedEnv?.storage||storage)?.removeItem(`cofresh.report.v2:${token}`) } catch { /* restricted storage */ }
        return { start() {}, stop() {}, event() {} }
    }
    const env=suppliedEnv || { window,document,storage,crypto:window.crypto,fetch:window.fetch.bind(window),
        beacon: navigator.sendBeacon?.bind(navigator),now:()=>Date.now(),
        setInterval:window.setInterval.bind(window),clearInterval:window.clearInterval.bind(window),
        setTimeout:window.setTimeout.bind(window),clearTimeout:window.clearTimeout.bind(window),AbortController }
    const key=`cofresh.report.v2:${token}`
    let saved
    try { saved=JSON.parse(env.storage.getItem(key)) } catch { /* private mode/storage restrictions */ }
    let state=saved && Array.isArray(saved.queue) && saved.visit?.id ? saved : {queue:[],visit:null}
    let visible=env.document.visibilityState==='visible',lastTick=env.now(),running=null,interval=null,started=false
    const persist=()=>{ try { env.storage.setItem(key,JSON.stringify(state)) } catch { /* tracking still works in memory */ } }
    const make=(type,payload={},id=env.crypto.randomUUID())=>({schema_version:2,event_id:id,visit_id:state.visit.id,event_type:type,payload})
    const enqueue=event=>{
        if (state.queue.some(e=>e.event_id===event.event_id)) return
        if (event.event_type==='time_spent') state.queue=state.queue.filter(e=>e.event_type!=='time_spent'||e.visit_id!==event.visit_id)
        state.queue.push(event)
        // Preserve visit registration; bound retry storage without growing indefinitely.
        if (state.queue.length>100) {
            const visitItems=state.queue.filter(e=>e.event_type==='visit')
            const dropped=new Set(visitItems.slice(0,-20).map(e=>e.visit_id))
            state.queue=[...visitItems.slice(-20),...state.queue.filter(e=>e.event_type!=='visit'&&!dropped.has(e.visit_id)).slice(-80)]
        }
        persist()
    }
    const ensureVisit=()=>{
        const now=env.now()
        if (!state.visit || now-state.visit.lastActivity>=15*60000 || now<state.visit.lastActivity) {
            if (state.visit) time(true)
            state.visit={id:env.crypto.randomUUID(),lastActivity:now,activeMs:0,lastQueuedMs:0,seen:{}}
            enqueue(make('visit',{},state.visit.id))
        }
    }
    const account=()=>{
        const now=env.now()
        if (visible && state.visit) {
            const until=Math.min(now,state.visit.lastActivity+120000)
            state.visit.activeMs=Math.min(86400000,state.visit.activeMs+Math.max(0,until-lastTick))
        }
        lastTick=now; persist()
    }
    const time=(force=false)=>{
        if (!state.visit) return
        if (state.visit.activeMs>state.visit.lastQueuedMs && (force || state.visit.activeMs-state.visit.lastQueuedMs>=15000)) {
            state.visit.lastQueuedMs=state.visit.activeMs
            enqueue(make('time_spent',{active_ms:Math.floor(state.visit.activeMs)}))
        }
    }
    const flush=()=>{
        if (running) return running
        running=(async()=>{
            for (const item of state.queue.slice(0,20)) {
                const controller=new env.AbortController()
                const timer=env.setTimeout(()=>controller.abort(),5000)
                try {
                    const response=await env.fetch('/api/reports/client-interest/'+token,{method:'POST',keepalive:true,
                        headers:{'Content-Type':'application/json'},body:JSON.stringify(item),signal:controller.signal})
                    if (!response.ok && ![400,404].includes(response.status)) break
                    state.queue=state.queue.filter(e=>e.event_id!==item.event_id); persist()
                } catch { break } finally { env.clearTimeout(timer) }
            }
        })().finally(()=>{running=null})
        return running
    }
    const event=(type,payload={},userAction=true)=>{
        if (!visible) return
        account(); time(true)
        if (userAction) { ensureVisit(); state.visit.lastActivity=env.now() }
        if (!state.visit) return
        const unique=type==='photo_open'?`${type}:${payload.photo_key}`:type
        if (!state.visit.seen[unique]) {
            state.visit.seen[unique]=true; enqueue(make(type,payload))
        }
        persist(); void flush()
    }
    const activity=()=>{
        if (!visible) return
        account(); time(); ensureVisit(); state.visit.lastActivity=env.now(); persist()
        if (isAtEnd() && !state.visit.seen.scroll_end) event('scroll_end',{},false)
    }
    const visibility=()=>{
        account(); time(true)
        visible=env.document.visibilityState==='visible'
        if (visible) { ensureVisit(); state.visit.lastActivity=env.now(); lastTick=env.now(); persist() }
        void flush()
    }
    const pagehide=()=>{
        account(); time(true); visible=false; persist()
        // Beacon acceptance is not a server ACK. Keep IDs for a safe retry on reload.
        if (env.beacon) for (const item of state.queue.slice(0,20)) {
            try { env.beacon('/api/reports/client-interest/'+token,new Blob([JSON.stringify(item)],{type:'application/json'})) } catch { /* retry persisted queue later */ }
        }
        void flush()
    }
    const actions=['pointerdown','keydown','touchstart','scroll']
    return {
        start() {
            if (started) return
            started=true
            if (visible) { ensureVisit(); enqueue(make('visit',{},state.visit.id)); state.visit.lastActivity=env.now(); persist() }
            for (const name of actions) env.window.addEventListener(name,activity,{passive:true})
            env.document.addEventListener('visibilitychange',visibility)
            env.window.addEventListener('pagehide',pagehide)
            env.window.addEventListener('pageshow',visibility)
            interval=env.setInterval(()=>{
                account(); time()
                if (visible && state.visit && !state.visit.seen.scroll_end && isAtEnd()) event('scroll_end',{},false)
                void flush()
            },5000)
            void flush()
        },
        stop() {
            if (!started) return
            account(); time(true); void flush(); started=false
            env.clearInterval(interval)
            for (const name of actions) env.window.removeEventListener(name,activity)
            env.document.removeEventListener('visibilitychange',visibility)
            env.window.removeEventListener('pagehide',pagehide)
            env.window.removeEventListener('pageshow',visibility)
        },
        event,
    }
}
